
import {Component, Prop, Vue} from "vue-property-decorator";
import OkDialog from "@/components/common/OkDialog.vue";
import {dataFieldsEditModule} from "@/store";
import DataFieldCache from "../../../models/data.field.cache";
import DataFieldBar from "@/components/study/datafields/base/DataFieldBar.vue";
import {MdbsCore} from "@/api/tsclient";
import {translate} from "@/auxillary/translation";
import {stringify} from "@/auxillary/datafield.stringify";
import EnumValueDefinitionDto = MdbsCore.EnumValueDefinitionDto;
import Hints from "@/components/study/datafields/base/Hints.vue";

import {findEnumValueSet, activeEnumValues} from "@/auxillary/enum.lookup";

@Component({
  name: 'LabValueField',
  components: {
    DataFieldBar,
    OkDialog,
    Hints
  },
})
export default class LabValueField extends Vue {

  @Prop({required: true})
  private dataField: DataFieldCache

  errorMessage: string = "";

  getEnumDisplayName(enumValue: EnumValueDefinitionDto) {
    return translate(enumValue.displayKey);
  }

  get labelField() {
    return this.displayName.length <= 4 ? this.displayName : ""
  }

  get labelAbove() {
    return this.displayName.length > 4 ? this.displayName : ""
  }

  get isVisible() {
    return this.field.isVisible;
  }

  get isReadonly() {
    return !this.field.isEditable;
  }

  get value() {
    this.validateInput()
    const value = this.dataField.values?.value;
    if (value === "unknown") {
      return "";
    }
    if (value === "not_executed") {
      return "";
    }
    return value;
  }

  get unit() {
    return this.dataField.values?.unit
  }

  set unit(unit: string) {
    dataFieldsEditModule.setValue({field: this.dataField, key: "unit", value: unit})
  }

  stringifyValue() {
    return stringify(this.field)
  }

  set value(value: string) {
    this.validateInput()
    dataFieldsEditModule.setValue({field: this.dataField, key: "value", value})
  }

  updateValue() {
    let value = "";

    if (this.unknown && this.canBeUnknown) {
      value = "unknown";
      this.unit = "";
      dataFieldsEditModule.setValue({field: this.dataField, key: "notExecuted", value: false})
    }

    if (this.notExecuted && this.canBeNotExecuted) {
      value = "not_executed";
      this.unit = "";
      dataFieldsEditModule.setValue({field: this.dataField, key: "unknown", value: false})
    }

    this.value = value;
  }

  get canBeUnknown() {
    return this.field.definition.canBeUnknown ?? false;
  }

  get unknown() {
    return this.dataField.values?.unknown ?? false
  }

  set unknown(value: boolean) {
    dataFieldsEditModule.setValue({field: this.dataField, key: "unknown", value})
    this.updateValue()
  }

  get canBeNotExecuted() {
    return this.field.definition.canBeNotExecuted ?? false;
  }

  get notExecuted() {
    return this.dataField.values?.notExecuted ?? false;
  }

  set notExecuted(value: boolean) {
    dataFieldsEditModule.setValue({field: this.dataField, key: "notExecuted", value})
    this.updateValue()
  }

  get unitDisplayName() {
    return "Einheit"; // TODO translate
  }

  get field() {
    return this.dataField;
  }

  get units() {
    const allUnits: EnumValueDefinitionDto[] = activeEnumValues(this.unitsEnum);
    const allowedUnits = this.field?.definition?.allowedUnits ?? [];

    // No restriction in the definition means every unit of the enum is offered.
    // Filtering unconditionally would leave the user with an empty, unusable dropdown.
    if (allowedUnits.length > 0)
      return allUnits?.filter(e => allowedUnits.includes(e.code)) ?? [];
    return allUnits;
  }

  get unitsEnum() {
    return findEnumValueSet(this.dataField.definition.unitEnum);
  }

  get minValue() {
    return this.field.definition.min;
  }

  get maxValue() {
    return this.field.definition.max;
  }

  get decimalSeparator() {
    return this.field.definition.decimalSeparator || ',';
  }

  validateKeyPress(evt: KeyboardEvent) {
    let keysAllowed: string[] = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    const keyPressed: string = evt.key;

    const value = this.dataField.values?.value;
    if (!value || (value && this.decimalSeparator && value.indexOf(this.decimalSeparator) == -1)) {
      keysAllowed.push(this.decimalSeparator)
    }

    if (!keysAllowed.includes(keyPressed)) {
      evt.preventDefault()
    }
  }

  get displayName() {
    if (!this.dataField.displayName) return ""
    return this.dataField.displayName + (this.isRequired ? "<span style='color:red;'>*</span>" : "");
  }

  get isRequired() {
    return this.dataField.isRequired
  }

  validateInput() {
    const value = this.dataField.values?.value;
    if (value) {
      const intValue = Number.parseInt(value);
      if (this.minValue) {
        if (intValue < this.minValue) {
          this.errorMessage = "Minimalwert: " + this.minValue;
          return;
        }
      }
      if (this.maxValue) {
        if (intValue > this.maxValue) {
          this.errorMessage = "Maximalwert: " + this.maxValue;
          return;
        }
      }
    }
    this.errorMessage = "";
  }
}
